import React, { useEffect, useRef } from 'react';
import { S13_INCIDENT_TYPES } from '../../functions/src/common/s13Workflow';
import type { S13ChecklistItem, S13ChecklistSection } from '../../functions/src/common/s13Checklist';

type Props = { items: S13ChecklistItem[]; reviewed: Set<string>; flaggedIds: Set<string>;
    disabled: boolean; busy: boolean; unsaved: boolean; onToggle: (item: S13ChecklistItem) => void;
    onSave: () => void; onClose: () => void };

const sections: Array<{ id: S13ChecklistSection; title: string }> = [
    { id: 'matrix', title: 'Incident classifications' }, { id: 'cards', title: 'Response cards' },
    { id: 'contacts', title: 'Contacts' }, { id: 'legal', title: 'Legal duties and insurance' },
];

const S13VerificationChecklist: React.FC<Props> = ({ items, reviewed, flaggedIds, disabled, busy,
    unsaved, onToggle, onSave, onClose }) => {
    const panelRef = useRef<HTMLElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const completed = items.filter(value => reviewed.has(value.token)).length;

    useEffect(() => {
        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        closeRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
                return;
            }
            if (event.key !== 'Tab')
                return;
            const focusable = panelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), summary');
            if (!focusable?.length)
                return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            previous?.focus();
        };
    }, [onClose]);

    const renderItem = (value: S13ChecklistItem) => <label key={value.id}
        className="block rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800">
        <span className="flex items-start gap-3"><input type="checkbox" checked={reviewed.has(value.token)} disabled={disabled || busy}
            onChange={() => onToggle(value)} className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600" />
            <span className="font-semibold">{value.title}</span>
            {flaggedIds.has(value.id) && <span className="ml-auto rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">Check trigger</span>}
        </span>
        <span className="mt-2 block whitespace-pre-line break-words pl-7 text-xs leading-relaxed text-slate-600">{value.detail}</span>
    </label>;

    return <div className="fixed inset-0 z-[200] flex justify-end">
        <button type="button" aria-label="Close verification checklist" onClick={onClose}
            className="absolute inset-0 bg-slate-950/55" />
        <aside ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="s13-checklist-title"
            className="relative flex h-full w-full max-w-lg flex-col bg-slate-50 shadow-2xl">
            <header className="flex items-start justify-between border-b border-slate-200 bg-white p-5">
                <div><h2 id="s13-checklist-title" className="text-xl font-black text-slate-900">Verification checklist</h2>
                    <p className="mt-1 text-sm text-slate-600">Track your review before validation.</p></div>
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Close checklist"
                    className="rounded-lg p-2 text-slate-600 hover:bg-slate-100">✕</button>
            </header>
            <div className="border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex justify-between text-sm font-bold text-slate-700"><span>Review progress</span>
                    <span>{completed} / {items.length}</span></div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-blue-600"
                    style={{ width: `${items.length ? completed / items.length * 100 : 0}%` }} /></div>
                <p className="mt-2 text-xs text-slate-500">Checks are optional. Matrix gap acknowledgement stays separate.</p>
            </div>
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
                {sections.map(section => {
                    const sectionItems = items.filter(value => value.section === section.id);
                    const sectionDone = sectionItems.filter(value => reviewed.has(value.token)).length;
                    return <section key={section.id}>
                        <h3 className="mb-2 flex justify-between text-sm font-black text-slate-900"><span>{section.title}</span>
                            <span>{sectionDone} / {sectionItems.length}</span></h3>
                        {section.id === 'matrix' ? <div className="space-y-2">{S13_INCIDENT_TYPES.map(incidentType => {
                            const cells = sectionItems.filter(value => value.incidentType === incidentType);
                            const done = cells.filter(value => reviewed.has(value.token)).length;
                            return <details key={incidentType} className="rounded-lg border border-slate-200 bg-white">
                                <summary className="cursor-pointer p-3 text-sm font-semibold text-slate-800">
                                    {incidentType} <span className="float-right text-slate-500">{done} / 4</span></summary>
                                <div className="space-y-2 border-t border-slate-100 bg-slate-50 p-2">{cells.map(renderItem)}</div>
                            </details>;
                        })}</div> : <div className="space-y-2">{sectionItems.map(renderItem)}</div>}
                    </section>;
                })}
            </div>
            <footer className="border-t border-slate-200 bg-white p-4">
                <p className="mb-3 text-xs text-slate-600">{disabled ? 'This review is complete.'
                    : unsaved ? 'Unsaved checks. Save Draft to keep progress.' : 'Checked progress is saved with this draft.'}</p>
                <div className="flex gap-2"><button type="button" onClick={onClose}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700">Close</button>
                    {!disabled && <button type="button" disabled={busy || !unsaved} onClick={onSave}
                        className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-40">Save Draft</button>}</div>
            </footer>
        </aside>
    </div>;
};

export default S13VerificationChecklist;
